import { useMemo } from 'react';
import { useClients, useContracts, useLinks, useConvenios } from '../contexts/DataContext';
import { getPrimaAmount } from '../utils/calculations';
import {
  getEstadoFianza,
  ESTADO_FIANZA,
  getEndososAbiertos,
  clasificarEndosoPendiente,
} from '../utils/fianzaEventos';

// A fianza still in play. Terminal ones (cancelada / anulada / sustituida) are
// finished business: they are not pipeline work, they are not up for renewal, and
// they no longer consume the cliente's línea de afianzamiento. Absent field means
// activa, so legacy fianzas need no migration.
const isFianzaActiva = (fianza) => getEstadoFianza(fianza) === ESTADO_FIANZA.ACTIVA;

// Required document types for client expedientes
export const REQUIRED_DOCS = [
  'actaConstitutiva',
  'constanciaSituacionFiscal',
  'actasAsambleas',
  'poderes',
  'comprobanteDomicilio',
  'identificacionOficial',
  'acuseReciboDeclaracion',
  'declaracionEjercicio',
  'estadosFinancierosExcel',
  'estadosFinancieros',
  'cedulaContador',
  'curriculumEmpresarial',
  'contrato'
];

// Human-readable labels for document types
export const DOC_LABELS = {
  actaConstitutiva: 'Acta Constitutiva',
  constanciaSituacionFiscal: 'Constancia Situación Fiscal',
  actasAsambleas: 'Actas de Asambleas',
  poderes: 'Poderes',
  comprobanteDomicilio: 'Comprobante de Domicilio',
  identificacionOficial: 'Identificación Oficial',
  acuseReciboDeclaracion: 'Acuse Declaración Anual',
  declaracionEjercicio: 'Declaración del Ejercicio',
  estadosFinancierosExcel: 'Estados Financieros (Excel)',
  estadosFinancieros: 'Estados Financieros (Contador)',
  cedulaContador: 'Cédula del Contador',
  curriculumEmpresarial: 'Currículum Empresarial',
  contrato: 'Contrato'
};

// Documents that expire (comprobanteDomicilio expires after 3 months)
const EXPIRING_DOCS = {
  comprobanteDomicilio: 3, // months
};

/**
 * Parse a date string in various formats to timestamp
 */
const parseDocumentDate = (dateStr) => {
  if (!dateStr) return null;

  // Try ISO format first
  let date = new Date(dateStr);
  if (!isNaN(date.getTime())) return date.getTime();

  // Try DD/MM/YYYY format (common in Mexico)
  const ddmmyyyy = dateStr.match(/(\d{1,2})[/-](\d{1,2})[/-](\d{4})/);
  if (ddmmyyyy) {
    date = new Date(ddmmyyyy[3], ddmmyyyy[2] - 1, ddmmyyyy[1]);
    if (!isNaN(date.getTime())) return date.getTime();
  }

  return null;
};

/**
 * Check if a document date is expired based on months limit
 */
const isDocumentExpired = (documentDate, monthsLimit) => {
  if (!documentDate) return false;

  const now = Date.now();
  const expirationDate = documentDate + (monthsLimit * 30 * 24 * 60 * 60 * 1000);
  return now > expirationDate;
};

/**
 * Check if a document is expiring soon (within 30 days of expiration)
 */
const isDocumentExpiringSoon = (documentDate, monthsLimit) => {
  if (!documentDate) return false;

  const now = Date.now();
  const expirationDate = documentDate + (monthsLimit * 30 * 24 * 60 * 60 * 1000);
  const warningDate = expirationDate - (30 * 24 * 60 * 60 * 1000); // 30 days before expiration

  return now > warningDate && now <= expirationDate;
};

/**
 * usePulsoData - Aggregates data for the Pulso dashboard
 * Uses preloaded data from DataContext for fast performance
 */
export function usePulsoData() {
  const { clientsList, clientsById, clientsLoading } = useClients();
  const { contractsList, contractsById, contractsLoading } = useContracts();
  const { linksById, linksLoading } = useLinks();
  const { conveniosById } = useConvenios();

  // Client Status: Categorize clients by document health
  const clientStatus = useMemo(() => {
    const categories = {
      ok: [],
      porVencer: [],
      vencidos: [],
      incompletos: [],
    };

    if (linksLoading) return categories;

    clientsList.forEach(client => {
      const clientLinks = (client.links || []).map(id => linksById[id]).filter(Boolean);

      // If no links, client has no expediente - mark as ok (no docs requested yet)
      if (clientLinks.length === 0) {
        categories.ok.push({
          ...client,
          requestedDocs: [],
          uploadedDocs: [],
          missingDocs: [],
          expiredDocs: [],
          expiringSoonDocs: [],
          status: 'ok',
          statusReason: 'Sin expediente',
        });
        return;
      }

      // Collect all requested documents across all links
      let requestedDocs = new Set();
      let uploadedDocs = new Set();
      let hasExpired = false;
      let hasExpiringSoon = false;
      let expiredDocs = [];
      let expiringSoonDocs = [];

      for (const link of clientLinks) {
        const files = link.files || {};
        const filesData = link.filesData || {};

        // Check each document type that exists in the link's files (requested)
        for (const docType of Object.keys(files)) {
          const fileInfo = files[docType];

          // Only count as requested if explicitly marked as required
          if (!fileInfo?.required) continue;

          requestedDocs.add(docType);

          // Check if document is uploaded
          if (fileInfo && fileInfo.uploadedCount > 0) {
            uploadedDocs.add(docType);

            // Check expiration for expiring docs
            if (EXPIRING_DOCS[docType]) {
              // Find the processed file data for this document type
              const processedFile = Object.values(filesData).find(f =>
                f.id && f.id.startsWith(docType)
              );

              let documentDate = null;

              // Try to get DocumentDate from results
              if (processedFile?.results?.DocumentDate?.value) {
                documentDate = parseDocumentDate(processedFile.results.DocumentDate.value);
              }

              // Fallback to uploadedAt if no DocumentDate
              if (!documentDate && fileInfo.fileNames?.[0]?.uploadedAt) {
                documentDate = fileInfo.fileNames[0].uploadedAt;
              }

              if (documentDate) {
                if (isDocumentExpired(documentDate, EXPIRING_DOCS[docType])) {
                  hasExpired = true;
                  expiredDocs.push(docType);
                } else if (isDocumentExpiringSoon(documentDate, EXPIRING_DOCS[docType])) {
                  hasExpiringSoon = true;
                  expiringSoonDocs.push(docType);
                }
              }
            }
          }
        }
      }

      // Missing docs = requested but not uploaded
      const missingDocs = [...requestedDocs].filter(doc => !uploadedDocs.has(doc));
      const hasAllRequired = missingDocs.length === 0;

      // Categorize client
      const enrichedClient = {
        ...client,
        hasAllRequired,
        requestedDocs: [...requestedDocs],
        uploadedDocs: [...uploadedDocs],
        missingDocs,
        expiredDocs,
        expiringSoonDocs,
        // Status flags for showing multiple tags
        isIncompleto: !hasAllRequired,
        isVencido: hasExpired,
        isPorVencer: hasExpiringSoon,
      };

      // Build status reason with all issues
      const reasons = [];
      if (!hasAllRequired) reasons.push(`Faltan: ${missingDocs.map(d => DOC_LABELS[d] || d).join(', ')}`);
      if (hasExpired) reasons.push(`Vencidos: ${expiredDocs.map(d => DOC_LABELS[d] || d).join(', ')}`);
      if (hasExpiringSoon) reasons.push(`Por vencer: ${expiringSoonDocs.map(d => DOC_LABELS[d] || d).join(', ')}`);
      enrichedClient.statusReason = reasons.length > 0 ? reasons.join(' | ') : 'Documentos al día';

      // Primary status for filtering (priority: incompleto > vencido > porVencer > ok)
      if (!hasAllRequired) {
        enrichedClient.status = 'incompleto';
        categories.incompletos.push(enrichedClient);
      } else if (hasExpired) {
        enrichedClient.status = 'vencido';
        categories.vencidos.push(enrichedClient);
      } else if (hasExpiringSoon) {
        enrichedClient.status = 'porVencer';
        categories.porVencer.push(enrichedClient);
      } else {
        enrichedClient.status = 'ok';
        categories.ok.push(enrichedClient);
      }
    });

    return categories;
  }, [clientsList, linksById, linksLoading]);

  // Pipeline: Categorize fianzas and fianzasRequeridas through their lifecycle
  // - pendientes: fianzasRequeridas with estatus pendiente (not yet started)
  // - en_proceso: fianzasRequeridas with estatus en_proceso (being worked on)
  // - emitidas_sin_pagar: fianzas emitidas but not paid yet
  // - pagadas: fianzas that have been paid
  // - diferidas: fianzasRequeridas that are postponed until a certain date
  const pipeline = useMemo(() => {
    const now = Date.now();
    const categories = {
      pendientes: [],
      en_proceso: [],
      emitidas_sin_pagar: [],
      pagadas: [],
      diferidas: [],
    };

    contractsList.forEach((contract) => {
      const isConvenio = Boolean(contract.convenioId);
      const clientData = clientsById[contract.clientId] || {};
      const convenio = isConvenio ? conveniosById[contract.convenioId] : null;
      const beneficiario = isConvenio
        ? (convenio?.beneficiarios || []).find((b) => b.id === contract.beneficiarioId)
        : null;
      const clientName = isConvenio
        ? `${convenio?.nombre || 'Convenio'} — ${beneficiario?.nombre || ''}`
        : (clientData.nombreEmpresa || 'Sin cliente');
      const contractTitle = contract.tituloContrato?.value || 'Sin título';

      const fianzas = contract.fianzas || [];
      const fianzasRequeridas = contract.fianzasRequeridas?.value || [];

      // Track which fianzasRequeridas have linked fianzas
      const linkedFianzaRequeridaIds = new Set();

      // Process emitted fianzas first
      fianzas.forEach((fianza, idx) => {
        // Terminal fianzas (cancelada / anulada / sustituida) are done — they are
        // not work in progress and must not sit in the pipeline. Vencimientos and
        // Después del Término already skip them; this column did not, so an
        // anulada fianza kept showing up as if it were still live.
        if (!isFianzaActiva(fianza)) return;

        // Use fianza's createdAt, or inicioVigenciaFianza, or contract createdAt as fallback
        const sortDate = fianza.createdAt || fianza.inicioVigenciaFianza || contract.createdAt || 0;

        const enrichedFianza = {
          ...fianza,
          id: fianza.id || `${contract.id}-fianza-${idx}`,
          type: 'fianza', // Mark as actual fianza
          contractId: contract.id,
          contractTitle,
          clientId: contract.clientId,
          convenioId: contract.convenioId || null,
          isConvenio,
          clientName,
          sortDate,
          assignedUsers: [...(clientData?.assignedUsers || []), ...(contract?.assignedUsers || [])].filter((v, i, a) => a.indexOf(v) === i),
        };

        // Find linked fianzaRequerida if any
        const linkedRequerida = fianzasRequeridas.find(fr => fr.fianzaId === fianza.id);
        if (linkedRequerida) {
          enrichedFianza.linkedRequerida = linkedRequerida;
          linkedFianzaRequeridaIds.add(linkedRequerida.fianzaId);
        }

        if (fianza.pagada === true) {
          categories.pagadas.push(enrichedFianza);
        } else {
          categories.emitidas_sin_pagar.push(enrichedFianza);
        }
      });

      // Process fianzasRequeridas (only those not yet emitted)
      fianzasRequeridas.forEach((fr, idx) => {
        // Skip if this fianzaRequerida has a LIVE linked fianza (already counted
        // above). Matching against a terminal one would drop the requerida from
        // both lists: its fianza was anulada, so the requirement is open again.
        if (fr.fianzaId && fianzas.some((f) => f.id === fr.fianzaId && isFianzaActiva(f))) {
          return;
        }

        // Use fianzaRequerida's createdAt if available, otherwise contract's createdAt
        const sortDate = fr.createdAt || contract.createdAt || 0;

        // Parse contract end date for diferidas display
        const fechaTermino = contract.fechaTermino?.value;
        let contractEndDate = null;
        if (fechaTermino) {
          // Try parsing DD-MM-YYYY or DD/MM/YYYY format
          const match = fechaTermino.match(/(\d{1,2})[-/](\d{1,2})[-/](\d{4})/);
          if (match) {
            contractEndDate = new Date(match[3], match[2] - 1, match[1]).getTime();
          } else {
            // Try ISO format
            const parsed = new Date(fechaTermino);
            if (!isNaN(parsed.getTime())) {
              contractEndDate = parsed.getTime();
            }
          }
        }

        const enrichedRequerida = {
          ...fr,
          id: `${contract.id}-req-${idx}`,
          type: 'fianzaRequerida', // Mark as requirement
          contractId: contract.id,
          contractTitle,
          clientId: contract.clientId,
          convenioId: contract.convenioId || null,
          isConvenio,
          clientName,
          sortDate,
          contractEndDate,
          fechaTerminoStr: fechaTermino,
          assignedUsers: [...(clientData?.assignedUsers || []), ...(contract?.assignedUsers || [])].filter((v, i, a) => a.indexOf(v) === i),
        };

        // Calculate days until contract end for all fianzasRequeridas
        if (contractEndDate) {
          enrichedRequerida.daysUntilContractEnd = Math.ceil((contractEndDate - now) / (24 * 60 * 60 * 1000));
        }

        // Handle diferidas separately
        if (fr.estatus === 'diferida') {
          // Mark as needing attention if contract is expired or expiring within 30 days
          if (contractEndDate) {
            enrichedRequerida.needsAttention = enrichedRequerida.daysUntilContractEnd <= 30;
          }
          categories.diferidas.push(enrichedRequerida);
        } else if (fr.estatus === 'en_proceso') {
          categories.en_proceso.push(enrichedRequerida);
        } else if (fr.estatus === 'pendiente' || !fr.estatus) {
          categories.pendientes.push(enrichedRequerida);
        }
      });
    });

    // Sort all categories by sortDate (oldest first - they need attention sooner)
    ['pendientes', 'en_proceso', 'emitidas_sin_pagar', 'pagadas'].forEach(key => {
      categories[key].sort((a, b) => (a.sortDate || 0) - (b.sortDate || 0));
    });

    // Sort diferidas: needs attention first, then by contract end date
    categories.diferidas.sort((a, b) => {
      // Items needing attention come first
      if (a.needsAttention && !b.needsAttention) return -1;
      if (!a.needsAttention && b.needsAttention) return 1;
      // Then sort by contract end date (soonest first)
      const aDate = a.contractEndDate || Infinity;
      const bDate = b.contractEndDate || Infinity;
      return aDate - bDate;
    });

    return categories;
  }, [contractsList, clientsById, conveniosById]);

  // Expirations: Categorize fianzas by days until expiration
  const expirations = useMemo(() => {
    const now = Date.now();
    const day = 24 * 60 * 60 * 1000;

    const categories = {
      vencidas: [],
      under30: [],
      under60: [],
      under90: [],
      over90: [],
    };

    contractsList.forEach((contract) => {
      const clientData = clientsById[contract.clientId] || {};
      const fianzas = contract.fianzas || [];

      fianzas.forEach((fianza) => {
        if (!fianza.pagada) return;
        if (!isFianzaActiva(fianza)) return;

        const finVigencia = fianza.finVigenciaFianza;
        if (!finVigencia) return;

        const daysUntilExpiry = Math.ceil((finVigencia - now) / day);

        const enrichedFianza = {
          ...fianza,
          contractId: contract.id,
          contractTitle: contract.tituloContrato?.value || 'Sin título',
          clientId: contract.clientId,
          clientName: clientData.nombreEmpresa || 'Sin cliente',
          daysUntilExpiry,
          prima: getPrimaAmount(fianza),
        };

        if (daysUntilExpiry < 0) {
          categories.vencidas.push(enrichedFianza);
        } else if (daysUntilExpiry <= 30) {
          categories.under30.push(enrichedFianza);
        } else if (daysUntilExpiry <= 60) {
          categories.under60.push(enrichedFianza);
        } else if (daysUntilExpiry <= 90) {
          categories.under90.push(enrichedFianza);
        } else {
          categories.over90.push(enrichedFianza);
        }
      });
    });

    // Sort: vencidas by most recently expired first, others by soonest to expire
    categories.vencidas.sort((a, b) => b.daysUntilExpiry - a.daysUntilExpiry);
    ['under30', 'under60', 'under90', 'over90'].forEach(key => {
      categories[key].sort((a, b) => a.daysUntilExpiry - b.daysUntilExpiry);
    });

    return categories;
  }, [contractsList, clientsById]);

  // Counts
  const pipelineCounts = useMemo(() => ({
    pendientes: pipeline.pendientes.length,
    en_proceso: pipeline.en_proceso.length,
    emitidas_sin_pagar: pipeline.emitidas_sin_pagar.length,
    pagadas: pipeline.pagadas.length,
    diferidas: pipeline.diferidas.length,
    diferidasNeedAttention: pipeline.diferidas.filter(p => p.needsAttention).length,
    total: pipeline.pendientes.length + pipeline.en_proceso.length +
           pipeline.emitidas_sin_pagar.length + pipeline.pagadas.length,
  }), [pipeline]);

  const expirationCounts = useMemo(() => ({
    vencidas: expirations.vencidas.length,
    under30: expirations.under30.length,
    under60: expirations.under60.length,
    under90: expirations.under90.length,
    over90: expirations.over90.length,
    total: expirations.vencidas.length + expirations.under30.length +
           expirations.under60.length + expirations.under90.length +
           expirations.over90.length,
  }), [expirations]);

  // Después del Término: fianzasRequeridas with momento === 'despues_termino'
  const despuesTermino = useMemo(() => {
    const now = Date.now();
    const day = 24 * 60 * 60 * 1000;
    const items = [];

    contractsList.forEach((contract) => {
      const clientData = clientsById[contract.clientId] || {};
      const clientName = clientData.nombreEmpresa || 'Sin cliente';
      const contractTitle = contract.tituloContrato?.value || 'Sin título';

      const fianzas = contract.fianzas || [];
      const fianzasRequeridas = contract.fianzasRequeridas?.value || [];

      // Parse contract end date
      const fechaTermino = contract.fechaTermino?.value;
      let contractEndDate = null;
      if (fechaTermino) {
        const match = fechaTermino.match(/(\d{1,2})[-/](\d{1,2})[-/](\d{4})/);
        if (match) {
          contractEndDate = new Date(match[3], match[2] - 1, match[1]).getTime();
        } else {
          const parsed = new Date(fechaTermino);
          if (!isNaN(parsed.getTime())) contractEndDate = parsed.getTime();
        }
      }

      fianzasRequeridas.forEach((fr, idx) => {
        // Only include explicit 'despues_termino' (missing momento defaults to 'antes_inicio')
        if (fr.momento !== 'despues_termino') return;

        // Skip if this fianzaRequerida already has a linked emitted fianza
        if (fr.fianzaId && fianzas.some(f => f.id === fr.fianzaId)) return;

        // Build sibling info: all other fianzas + fianzasRequeridas in the same contract
        const siblings = [];
        let latestSiblingExpiry = null;

        // Sibling fianzasRequeridas (non-despues_termino)
        fianzasRequeridas.forEach((siblingFr, sibIdx) => {
          if (sibIdx === idx) return; // skip self

          // Check if this sibling has a linked emitted fianza
          const linkedFianza = siblingFr.fianzaId
            ? fianzas.find(f => f.id === siblingFr.fianzaId)
            : null;

          let status;
          let finVigencia = null;
          let daysUntilExpiry = null;

          if (linkedFianza) {
            status = linkedFianza.pagada ? 'pagada' : 'emitida';
            finVigencia = linkedFianza.finVigenciaFianza || null;
            if (finVigencia) {
              daysUntilExpiry = Math.ceil((finVigencia - now) / day);
              if (!latestSiblingExpiry || finVigencia > latestSiblingExpiry) {
                latestSiblingExpiry = finVigencia;
              }
            }
          } else {
            status = siblingFr.estatus || 'pendiente';
          }

          siblings.push({
            tipo: siblingFr.tipo || 'Sin tipo',
            momento: siblingFr.momento || 'antes_inicio',
            status,
            finVigencia,
            daysUntilExpiry,
          });
        });

        // Fianzas without a linked fianzaRequerida (standalone emitted fianzas)
        fianzas.forEach((fianza) => {
          // Terminal fianzas (cancelada/anulada) are excluded from siblings.
          if (!isFianzaActiva(fianza)) return;
          const hasLinkedRequerida = fianzasRequeridas.some(r => r.fianzaId === fianza.id);
          if (!hasLinkedRequerida) {
            const status = fianza.pagada ? 'pagada' : 'emitida';
            const finVigencia = fianza.finVigenciaFianza || null;
            let daysUntilExpiry = null;
            if (finVigencia) {
              daysUntilExpiry = Math.ceil((finVigencia - now) / day);
              if (!latestSiblingExpiry || finVigencia > latestSiblingExpiry) {
                latestSiblingExpiry = finVigencia;
              }
            }
            siblings.push({
              tipo: fianza.tipo || 'Sin tipo',
              momento: 'antes_inicio',
              status,
              finVigencia,
              daysUntilExpiry,
            });
          }
        });

        // allSiblingsComplete: all non-despues_termino siblings are emitida or pagada
        const nonDtSiblings = siblings.filter(s => s.momento !== 'despues_termino');
        const allSiblingsComplete = nonDtSiblings.length > 0 &&
          nonDtSiblings.every(s => s.status === 'emitida' || s.status === 'pagada');

        const estatus = fr.estatus || 'pendiente';
        const needsAttention = allSiblingsComplete && estatus === 'pendiente';

        const sortDate = fr.createdAt || contract.createdAt || 0;

        items.push({
          ...fr,
          id: `${contract.id}-req-${idx}`,
          type: 'fianzaRequerida',
          contractId: contract.id,
          contractTitle,
          clientId: contract.clientId,
          clientName,
          sortDate,
          contractEndDate,
          fechaTerminoStr: fechaTermino,
          assignedUsers: [...(clientData?.assignedUsers || []), ...(contract?.assignedUsers || [])].filter((v, i, a) => a.indexOf(v) === i),
          siblings,
          latestSiblingExpiry,
          latestSiblingExpiryDays: latestSiblingExpiry ? Math.ceil((latestSiblingExpiry - now) / day) : null,
          allSiblingsComplete,
          needsAttention,
          daysUntilContractEnd: contractEndDate ? Math.ceil((contractEndDate - now) / day) : null,
        });
      });
    });

    // Sort: needsAttention first, then by sortDate
    items.sort((a, b) => {
      if (a.needsAttention && !b.needsAttention) return -1;
      if (!a.needsAttention && b.needsAttention) return 1;
      return (a.sortDate || 0) - (b.sortDate || 0);
    });

    return items;
  }, [contractsList, clientsById]);

  const despuesTerminoCounts = useMemo(() => ({
    total: despuesTermino.length,
    diferidas: despuesTermino.filter(i => (i.estatus || 'pendiente') === 'diferida').length,
    needsAttention: despuesTermino.filter(i => i.needsAttention).length,
  }), [despuesTermino]);

  // Count clients by status flags (allows double counting)
  // A client can be both incompleto AND vencido, so we count by flags, not category arrays
  const clientCounts = useMemo(() => {
    const allClients = [
      ...clientStatus.ok,
      ...clientStatus.porVencer,
      ...clientStatus.vencidos,
      ...clientStatus.incompletos,
    ];

    return {
      ok: allClients.filter(c => !c.isIncompleto && !c.isVencido && !c.isPorVencer).length,
      porVencer: allClients.filter(c => c.isPorVencer).length,
      vencidos: allClients.filter(c => c.isVencido).length,
      incompletos: allClients.filter(c => c.isIncompleto).length,
      total: clientsList.length,
    };
  }, [clientStatus, clientsList.length]);

  // Endosos the agente was asked for and has not applied yet. Purely derived —
  // the reminder is a red number in Pulso, not a push (there is no scheduler in
  // this codebase yet).
  const endososPendientes = useMemo(() => {
    if (contractsLoading) return [];
    const now = Date.now();
    const out = [];

    contractsList.forEach((contract) => {
      const clientData = clientsById[contract.clientId] || {};
      const isConvenio = Boolean(contract.convenioId);
      const clientName = isConvenio
        ? (conveniosById[contract.convenioId]?.nombre || 'Convenio')
        : (clientData.nombreEmpresa || 'Cliente sin nombre');
      const contractTitle = contract.tituloContrato?.value || 'Contrato sin título';

      (contract.fianzas || []).forEach((fianza) => {
        // A terminal fianza cannot be endorsed, so its open requests are moot.
        if (!isFianzaActiva(fianza)) return;

        getEndososAbiertos(fianza).forEach((pendiente) => {
          const urgencia = clasificarEndosoPendiente(pendiente, now);
          out.push({
            ...pendiente,
            urgencia: urgencia.code,
            diasRestantes: urgencia.dias,
            fianzaId: fianza.id,
            numeroFianza: fianza.numeroFianza || 'sin número',
            contractId: contract.id,
            contractTitle,
            clientId: contract.clientId,
            convenioId: contract.convenioId || null,
            isConvenio,
            clientName,
            assignedUsers: [
              ...(clientData.assignedUsers || []),
              ...(contract.assignedUsers || []),
            ].filter((v, i, a) => a.indexOf(v) === i),
          });
        });
      });
    });

    // Overdue first, then soonest; undated last — they have no clock running.
    const ORDER = { vencido: 0, urgente: 1, proximo: 2, normal: 3, sinFecha: 4 };
    return out.sort((a, b) => {
      if (ORDER[a.urgencia] !== ORDER[b.urgencia]) return ORDER[a.urgencia] - ORDER[b.urgencia];
      return (a.fechaLimite || Infinity) - (b.fechaLimite || Infinity);
    });
  }, [contractsList, clientsById, conveniosById, contractsLoading]);

  const endososPendientesCounts = useMemo(() => ({
    total: endososPendientes.length,
    vencidos: endososPendientes.filter((e) => e.urgencia === 'vencido').length,
    urgentes: endososPendientes.filter((e) => e.urgencia === 'urgente').length,
    proximos: endososPendientes.filter((e) => e.urgencia === 'proximo').length,
    sinFecha: endososPendientes.filter((e) => e.urgencia === 'sinFecha').length,
    // What the tab badge turns red for.
    needAttention: endososPendientes.filter(
      (e) => e.urgencia === 'vencido' || e.urgencia === 'urgente'
    ).length,
  }), [endososPendientes]);

  return {
    // Data
    pipeline,
    expirations,
    endososPendientes,
    clientStatus,
    despuesTermino,
    clientsList,
    clientsById,
    contractsList,
    contractsById,
    linksById,

    // Counts
    pipelineCounts,
    expirationCounts,
    endososPendientesCounts,
    clientCounts,
    despuesTerminoCounts,

    // Loading states
    loading: clientsLoading || contractsLoading || linksLoading,
    clientsLoading,
    contractsLoading,
    linksLoading,
  };
}

export default usePulsoData;
